import { createMatchApi, updateMatchApi } from "@/api/services/admin/match";
import { clearContractToken, useProductContractHandoff } from "@/api/services/admin/product-contract";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { MatchBasicForm } from "@/features/match/components/MatchForm/MatchBasicForm";
import { MatchContractForm } from "@/features/match/components/MatchForm/MatchContractForm";
import { MatchOtherInfoForm } from "@/features/match/components/MatchForm/MatchOtherInfoForm";
import { matchSchema } from "@/features/match/schema/matchSchema";
import { TMatchForm } from "@/features/match/types/matchForm";
import { convertMatchFormToCreateReq, convertMatchFormToUpdateReq } from "@/features/match/utils/converts";
import { useToast } from "@/hooks/useToast";
import { useTriggerValidate } from "@/hooks/useTriggerValidate";
import { AppRoutes } from "@/routes/config";
import { dayjs } from "@/utils/dayjs";
import { setErrorForm, showError } from "@/utils/error";
import { getCenterFrom, getDynamicRoute, redirectToCenter } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TMatchFormProps = {
  previousData?: Partial<TMatchForm>;
};

const initCreateForm = {
  matchName: "",
  matchLicenseNumber: `${dayjs().format("YY")}-ユ-`,
  contractStatus: "",
  contractPlan: "",
  description: "",
  note: "",
};

export const MatchForm = forwardRef<{ submit: () => void }, TMatchFormProps>(({ previousData }, ref) => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const methods = useForm<TMatchForm>({
    defaultValues: previousData ?? initCreateForm,
    resolver: yupResolver(matchSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { contract } = useProductContractHandoff();
  const [loading, setLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  useTriggerValidate({ methods, name: "contractStatus", triggerFields: ["contractStartDate"] });
  useTriggerValidate({ methods, name: "contractStartDate", triggerFields: ["contractEndDate"] });

  // The Center already knows the organization name, so the create form starts pre-filled with it.
  useEffect(() => {
    const matchName = methods.getValues("matchName");
    if (!matchName && contract?.organizationName) {
      methods.resetField("matchName", { defaultValue: contract.organizationName });
    }
  }, [contract]);

  // Leaving the page is deferred to an effect so it runs after the form is marked submitted
  // (otherwise the unsaved-changes blocker would catch the navigation).
  useEffect(() => {
    if (!isSaved) return;

    if (isEditMode) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MATCH.FETCH_DETAIL, id] });
      navigate(getDynamicRoute(AppRoutes.matchDetail, { id }));
      return;
    }

    // The hand-off is consumed — drop the token so the create flow closes again.
    clearContractToken();

    // 遷移元のcenterパスが分かっていれば、その画面へそのまま戻す。
    const centerFrom = getCenterFrom();
    if (centerFrom) {
      redirectToCenter(centerFrom);
      return;
    }

    if (!contract) {
      const centerOrganizationList = "/organizations";
      redirectToCenter(centerOrganizationList);
      return;
    }
    const centerOrganizationDetail = getDynamicRoute("/organizations/:id", { id: contract.organizationId });
    redirectToCenter(centerOrganizationDetail);
  }, [isSaved]);

  const onSubmit = async (data: TMatchForm) => {
    setLoading(true);
    try {
      const finalData: TMatchForm = {
        ...data,
      };

      if (isEditMode) {
        const payload = convertMatchFormToUpdateReq(finalData);
        const rs = await updateMatchApi(id!, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        setIsSaved(true);
      } else {
        // Creation is only reachable from a Center hand-off, so the pending contract must be attached.
        if (!contract) {
          setLoading(false);
          navigate(AppRoutes.duringContractList);
          return;
        }
        const payload = convertMatchFormToCreateReq(finalData, contract.organizationId, contract.productContractId);
        const rs = await createMatchApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        setIsSaved(true);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MATCH.FETCH_LIST] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      await methods.handleSubmit(onSubmit)();
    },
  }));

  const { id: matchId } = useParams();

  const backRoute = matchId ? getDynamicRoute(AppRoutes.matchDetail, { id: matchId }) : AppRoutes.duringContractList;

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={matchSchema} enableBlocker={!loading}>
      <MenuNavigate
        contents={[
          { title: "基本情報", content: <MatchBasicForm /> },
          { title: "契約情報", content: <MatchContractForm /> },
          { title: "その他（顧客未開示の情報）", content: <MatchOtherInfoForm /> },
        ]}
        contentWidth="900px"
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              {isEditMode ? (
                <Button size="sm-round" variant="secondary" onClick={() => navigate(backRoute)}>
                  戻る
                </Button>
              ) : (
                <span />
              )}
              <Stack direction="row" gap={3}>
                <Button disabled={loading} size="sm-round" type="submit">
                  保存
                </Button>
              </Stack>
            </Stack>
          </FooterContainer>
        }
      />
    </Form>
  );
});
